import { MouseEvent, useRef, useState } from 'react';
import {
  Checkboxes, ErrorMessage, Pagination, Table,
} from 'nhsuk-react-components';
import useNotifications from 'queries/fetch/useNotifications.ts';
import useMarkNotificationsAsRead from 'queries/mutate/markNotificationsAsRead.ts';
import useRemoveNotifications from 'queries/mutate/removeNotifications.ts';
import ProgressIndicator from 'components/ProgressIndicator';
import NoHoverTable from 'components/NoHoverTable';
import NotificationRow from './NotificationRow';
import './ActivityCentre.scss';

export const PAGE_SIZE = 10;

function ActivityCentre() {
  const { data: notifications, isLoading, error } = useNotifications();
  const markAsRead = useMarkNotificationsAsRead();
  const removeNotifications = useRemoveNotifications();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [requestedPage, setRequestedPage] = useState(1);
  const activityCentreRef = useRef<HTMLDivElement>(null);

  const totalPages = Math.max(1, Math.ceil((notifications?.length ?? 0) / PAGE_SIZE));
  const page = Math.min(requestedPage, totalPages);
  const pagedNotifications = notifications?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE) ?? [];

  const allIdsOnPage = pagedNotifications.map((notification) => notification.id);
  const allSelectedOnPage = allIdsOnPage.length > 0 && selectedIds.length === allIdsOnPage.length;
  const someSelectedOnPage = selectedIds.length > 0 && !allSelectedOnPage;
  const unreadSelectedIds = pagedNotifications
    .filter((notification) => !notification.read && selectedIds.includes(notification.id))
    .map((notification) => notification.id);

  const setIndeterminate = (node: HTMLInputElement | null) => {
    if (node) {
      // indeterminate has no JSX/HTML attribute equivalent, it can only be set via the DOM node
      // eslint-disable-next-line no-param-reassign
      node.indeterminate = someSelectedOnPage;
    }
  };

  const toggleAll = () => {
    setSelectedIds(allSelectedOnPage ? [] : allIdsOnPage);
  };

  const toggleOne = (id: string) => {
    setSelectedIds(
      selectedIds.includes(id)
        ? selectedIds.filter((selectedId) => selectedId !== id)
        : [...selectedIds, id],
    );
  };

  const goToPage = (event: MouseEvent<HTMLAnchorElement>, newPage: number) => {
    event.preventDefault();
    setRequestedPage(newPage);
    setSelectedIds([]);
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    activityCentreRef.current?.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  };

  const handleMarkAsRead = () => {
    const count = unreadSelectedIds.length;
    markAsRead.mutate({ ids: unreadSelectedIds }, {
      onSuccess: () => {
        setSelectedIds([]);
        setFeedbackMessage(`${count} Notification${count === 1 ? '' : 's'} successfully marked as read`);
      },
    });
  };

  const handleRemove = () => {
    const count = selectedIds.length;
    removeNotifications.mutate({ ids: selectedIds }, {
      onSuccess: () => {
        setSelectedIds([]);
        setFeedbackMessage(`${count} Notification${count === 1 ? '' : 's'} successfully removed`);
      },
    });
  };

  if (error) {
    return <ErrorMessage role="alert">There was an error getting your activity.</ErrorMessage>;
  }

  if (isLoading) {
    return <ProgressIndicator text="Loading activity..." />;
  }

  if (!notifications || notifications.length === 0) {
    return <p>You have no notifications.</p>;
  }

  return (
    <div className="activity-centre" ref={activityCentreRef}>
      <div className="activity-centre__actions">
        <span className="activity-centre__selected-count">{`${selectedIds.length} Selected`}</span>
        <button
          type="button"
          className="activity-centre__action-link"
          disabled={unreadSelectedIds.length === 0}
          onClick={handleMarkAsRead}
          style={{ marginLeft: 'auto' }}
        >
          Mark as read
        </button>
        <button
          type="button"
          className="activity-centre__action-link"
          disabled={selectedIds.length === 0}
          onClick={handleRemove}
          style={selectedIds.length > 0 ? { color: '#d5281b' } : {}}
        >
          Remove
        </button>
      </div>
      {feedbackMessage && (
        <p className="activity-centre__feedback" role="status">{feedbackMessage}</p>
      )}
      <NoHoverTable>
        <Table.Head>
          <Table.Row>
            <Table.Cell
              scope="col"
              style={{ paddingLeft: '15px', width: '84px' }}
            >
              <Checkboxes.Item
                id="select-all"
                checked={allSelectedOnPage}
                onChange={toggleAll}
                aria-label="Select all notifications"
                ref={setIndeterminate}
              >
                <div />
              </Checkboxes.Item>
            </Table.Cell>
            <Table.Cell scope="col" style={{ width: '40%', paddingLeft: '30px' }}>Type</Table.Cell>
            <Table.Cell scope="col" style={{ width: '20%' }}>Team</Table.Cell>
            <Table.Cell scope="col" style={{ width: '20%' }}>Time and date</Table.Cell>
            <Table.Cell scope="col" style={{ width: '84px' }}>
              <span className="nhsuk-u-visually-hidden">Actions</span>
            </Table.Cell>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          <>
            {pagedNotifications.map((notification) => (
              <NotificationRow
                key={notification.id}
                notification={notification}
                selected={selectedIds.includes(notification.id)}
                onToggleSelected={() => toggleOne(notification.id)}
              />
            ))}
          </>
        </Table.Body>
      </NoHoverTable>
      {totalPages > 1 && (
        <Pagination>
          {page > 1 && (
            <Pagination.Item
              previous
              href="#"
              onClick={(event) => goToPage(event, page - 1)}
              labelText={`Page ${page - 1}`}
            />
          )}
          {page < totalPages && (
            <Pagination.Item
              next
              href="#"
              onClick={(event) => goToPage(event, page + 1)}
              labelText={`Page ${page + 1}`}
            />
          )}
        </Pagination>
      )}
    </div>
  );
}

export default ActivityCentre;
